import { FilterTagsCondition } from 'dashboard/components/TagsConditionFilter';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Account } from 'dashboard/models/account';
import { Case, CaseStatus } from 'dashboard/models/case';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplate } from 'dashboard/models/case-template';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import Action from 'lib/interfaces/Action';
import { PaginationParams } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

export type CasesActions =
  | Action<CasesActionType.SetCases, SetCasesPayload>
  | Action<CasesActionType.SetCasesRequestKey, SetCasesRequestKeyPayload>
  | Action<CasesActionType.SetCasesDetails, SetCasesDetailsPayload>
  | Action<CasesActionType.SetAllCaseTemplates, SetAllCaseTemplatesPayload>
  | Action<CasesActionType.SetCurrentPage, SetCurrentPagePayload>
  | Action<CasesActionType.SetPageSize, SetPageSizePayload>
  | Action<CasesActionType.SetFilters, SetFiltersPayload>
  | Action<CasesActionType.SetSortParams, SetSortParamsPayload>;

export enum CasesActionType {
  SetCases = 'cases/SET_CASES',
  SetCasesRequestKey = 'cases/SET_CASES_REQUEST_KEY',
  SetCasesDetails = 'cases/SET_CASES_DETAILS',
  SetAllCaseTemplates = 'cases/SET_ALL_CASE_TEMPLATES',
  SetCurrentPage = 'cases/SET_CURRENT_PAGE',
  SetPageSize = 'cases/SET_PAGE_SIZE',
  SetFilters = 'cases/SET_FILTERS',
  SetSortParams = 'cases/SET_SORT_PARAMS',
}

interface SetCasesPayload {
  cases: Case[];
  key: string;
  paginationParams: PaginationParams;
}

export function setCases(
  cases: Case[],
  key: string,
  paginationParams: PaginationParams,
): Action<CasesActionType.SetCases, SetCasesPayload> {
  return {
    payload: {
      cases,
      key,
      paginationParams,
    },
    type: CasesActionType.SetCases,
  };
}

interface SetCasesRequestKeyPayload {
  key: string;
}

export function setCasesRequestKey(
  key: string,
): Action<CasesActionType.SetCasesRequestKey, SetCasesRequestKeyPayload> {
  return {
    payload: {
      key,
    },
    type: CasesActionType.SetCasesRequestKey,
  };
}

interface SetCasesDetailsPayload {
  accounts: Account[];
  caseQueues: CaseQueue[];
  caseTemplates: CaseTemplate[];
  users: User[];
  tags: Tag[];
}

export function setCasesDetails(
  accounts: Account[],
  caseQueues: CaseQueue[],
  caseTemplates: CaseTemplate[],
  users: User[],
  tags: Tag[],
): Action<CasesActionType.SetCasesDetails, SetCasesDetailsPayload> {
  return {
    payload: {
      accounts,
      caseQueues,
      caseTemplates,
      users,
      tags,
    },
    type: CasesActionType.SetCasesDetails,
  };
}

interface SetAllCaseTemplatesPayload {
  caseTemplates: CaseTemplate[];
}

export function setAllCaseTemplates(
  caseTemplates: CaseTemplate[],
): Action<CasesActionType.SetAllCaseTemplates, SetAllCaseTemplatesPayload> {
  return {
    payload: {
      caseTemplates,
    },
    type: CasesActionType.SetAllCaseTemplates,
  };
}

interface SetCurrentPagePayload {
  currentPage: number;
}

export function setCurrentPage(
  currentPage: number,
): Action<CasesActionType.SetCurrentPage, SetCurrentPagePayload> {
  return {
    payload: {
      currentPage,
    },
    type: CasesActionType.SetCurrentPage,
  };
}

interface SetPageSizePayload {
  pageSize: number;
}

export function setPageSize(
  pageSize: number,
): Action<CasesActionType.SetPageSize, SetPageSizePayload> {
  return {
    payload: {
      pageSize,
    },
    type: CasesActionType.SetPageSize,
  };
}

export type FilterValues = string[] | 'all';

export interface CasesFilterParams {
  startDate?: Iso8601DateString;
  endDate?: Iso8601DateString;
  dateType?: 'created_at' | 'resolved_at';
  status?: CaseStatus[];
  resolutions?: string[];
  caseStateNames?: FilterValues;
  caseTemplateIds?: FilterValues;
  caseQueueIds?: FilterValues;
  tagsConditions?: FilterTagsCondition[][];
  assigneeIds?: FilterValues;
  notAssigneeIds?: string[];
  teamAssigneeIds?: FilterValues;
  omni?: string;
  // These filters are dynamic and fields based
  filters?: object;
  isInitial?: boolean;
}

interface SetFiltersPayload {
  filters: CasesFilterParams;
}

export function setFilters(
  filters: CasesFilterParams,
): Action<CasesActionType.SetFilters, SetFiltersPayload> {
  return {
    payload: {
      filters,
    },
    type: CasesActionType.SetFilters,
  };
}

interface SetSortParamsPayload {
  sortParams: SortParams;
}

export function setSortParams(
  sortParams: SortParams,
): Action<CasesActionType.SetSortParams, SetSortParamsPayload> {
  return {
    payload: {
      sortParams,
    },
    type: CasesActionType.SetSortParams,
  };
}
